import {
  Bullet,
  Bullets,
  Callout,
  Fact,
  Internal,
  LegalDocument,
  Mail,
  Outbound,
  P,
  Pending,
  SubHeading,
  type LegalSection,
} from '../../components/legal';
import { COMPANY, PAYMENTS, SUBPROCESSORS } from '../../lib/company';
import { usePageTitle } from '../../lib/usePageTitle';

/**
 * Privacy Policy.
 *
 * Expected by every enterprise buyer who reads
 * a vendor page before signing. The descriptions of cookies, tokens and
 * retention below are written from what the code actually does — one HttpOnly
 * refresh cookie scoped to the auth routes, a non-identifying session hint in
 * localStorage, no analytics or advertising tags anywhere on the site — rather
 * than from a template. A privacy policy that describes tracking the site does
 * not do is not a safer policy; it is an inaccurate one.
 *
 * Unchosen subprocessors are gaps, not guesses. See `../../lib/company`.
 */
const UPDATED = '2026-09-15';

const SECTIONS: LegalSection[] = [
  {
    id: 'who-we-are',
    title: 'Who is responsible for your data',
    body: (
      <>
        <P>
          {COMPANY.product} is operated by{' '}
          <strong className="text-white">
            <Fact value={COMPANY.legalName} label="registered legal name" />
          </strong>
          , of <Fact value={COMPANY.registeredAddress} label="registered address" />. We are
          the controller for the personal data described here.
        </P>
        <P>
          For privacy questions, or to exercise any of the rights in this policy, email{' '}
          <Mail address={COMPANY.email.support} /> with &ldquo;Privacy&rdquo; in the subject
          line.
        </P>
        <Callout>
          Where you use {COMPANY.product} to test your own systems, any personal data inside
          the test targets and evidence remains yours. For that material you are the
          controller and we act as your processor, handling it only to run the service for
          you.
        </Callout>
      </>
    ),
  },
  {
    id: 'what-we-collect',
    title: 'What we collect',
    body: (
      <>
        <SubHeading>Account data</SubHeading>
        <P>
          Your email address, an optional display name, a hashed password, and the timestamps
          around registration, activation and sign-in. We never store your password itself.
        </P>

        <SubHeading>Subscription and billing data</SubHeading>
        <P>
          Your plan, subscription status, billing period, and the customer, subscription and
          transaction identifiers our payment processor returns to us.{' '}
          <strong className="text-white">
            We never see or store your card number.
          </strong>{' '}
          Card details are entered on a page operated by{' '}
          {PAYMENTS.gatewayName ?? 'our payment processor'} and stay with them.
        </P>

        <SubHeading>Usage and metering data</SubHeading>
        <P>
          Which agents ran, when, for how long, and the execution hours drawn against your
          allowance. This is what entitlement enforcement is based on, so it is kept for the
          life of the account and for as long as billing records must be retained.
        </P>

        <SubHeading>QA run content</SubHeading>
        <P>
          The specifications, acceptance criteria, configuration and targets you supply, and
          the reports and evidence the agent produces — which may include screenshots, logs
          and captured responses from the systems you test. If those systems contain personal
          data, the evidence may too. Point the agent at test environments and test data
          wherever you can.
        </P>

        <SubHeading>Technical data</SubHeading>
        <P>
          IP address, user agent, request paths and error diagnostics, recorded in server
          logs and in the audit trail for security-relevant actions such as sign-in, password
          change and credential rotation.
        </P>

        <SubHeading>Messages you send us</SubHeading>
        <P>
          Anything you email to support, sales or security, together with our replies, so we
          can follow up and keep a record of what was agreed.
        </P>
      </>
    ),
  },
  {
    id: 'why-we-use-it',
    title: 'Why we use it, and on what basis',
    body: (
      <Bullets>
        <Bullet>
          <strong className="text-white">To provide the service</strong> — running agents,
          producing reports, enforcing plan entitlements, and supporting you.{' '}
          <em>Basis: performance of our contract with you.</em>
        </Bullet>
        <Bullet>
          <strong className="text-white">To take payment</strong> — passing our payment
          processor what it needs to bill you, and reconciling its confirmations against your
          account.{' '}
          <em>Basis: performance of our contract, and legal obligation for tax records.</em>
        </Bullet>
        <Bullet>
          <strong className="text-white">To keep the service secure</strong> — rate limiting,
          abuse detection, audit trails and investigating incidents.{' '}
          <em>Basis: our legitimate interest in a service that is not abused.</em>
        </Bullet>
        <Bullet>
          <strong className="text-white">To communicate with you</strong> — activation, one-time
          codes, password resets, billing notices and material changes to these documents.{' '}
          <em>Basis: performance of our contract.</em>
        </Bullet>
        <Bullet>
          <strong className="text-white">To improve the product</strong> — using aggregate
          patterns such as failure rates and run durations.{' '}
          <em>Basis: our legitimate interest, using aggregated data rather than your content.</em>
        </Bullet>
      </Bullets>
    ),
  },
  {
    id: 'what-we-dont-do',
    title: 'What we do not do',
    body: (
      <Bullets>
        <Bullet>We do not sell or rent your personal data. There is no exception to this.</Bullet>
        <Bullet>
          We do not use your QA content, specifications or evidence to train machine-learning
          models, our own or anyone else&rsquo;s.
        </Bullet>
        <Bullet>
          We do not run advertising or analytics trackers on this site. There is no
          third-party tag on the page you are reading.
        </Bullet>
        <Bullet>
          We do not share your data with anyone beyond the processors listed below and the
          cases in the disclosures section.
        </Bullet>
      </Bullets>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies and local storage',
    body: (
      <>
        <P>
          The site uses the minimum needed to keep you signed in. There are no advertising,
          profiling or analytics cookies, so there is no consent banner to dismiss.
        </P>
        <Bullets>
          <Bullet>
            <strong className="text-white">Refresh token cookie</strong> — set when you sign
            in. It is <code className="text-hull-200">HttpOnly</code>,{' '}
            <code className="text-hull-200">Secure</code>,{' '}
            <code className="text-hull-200">SameSite=Strict</code> and scoped to the
            authentication routes, so page scripts cannot read it and it is not sent to any
            other site. Strictly necessary.
          </Bullet>
          <Bullet>
            <strong className="text-white">Session hint</strong> — a single flag in your
            browser&rsquo;s local storage recording only that a session may exist, so a page
            reload does not flash the login screen. It contains no personal data and no token.
          </Bullet>
          <Bullet>
            <strong className="text-white">Access token</strong> — held in memory only, and
            deliberately not persisted, so closing the tab discards it.
          </Bullet>
        </Bullets>
        <P>
          The payment page sets its own cookies when it loads. Those are set by{' '}
          {PAYMENTS.gatewayName ?? 'our payment processor'} rather than by us, and are
          governed by their privacy notice &mdash; we do not receive or store your full card
          number at any point.
        </P>
      </>
    ),
  },
  {
    id: 'subprocessors',
    title: 'Who else processes your data',
    body: (
      <>
        <P>
          We use a small number of processors, each bound by contract to process data only on
          our instructions and to protect it appropriately. The last row is not one of ours:
          it is listed because a model provider is the one part of the pipeline you choose
          yourself, and you are entitled to see that we do not choose it for you.
        </P>
        <ul className="mt-5 divide-y divide-hull-700 overflow-hidden rounded-card border border-hull-700">
          {SUBPROCESSORS.map((processor) => (
            <li
              key={processor.purpose}
              className="flex flex-col gap-1 bg-hull-800/40 px-4 py-3.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6"
            >
              <span className="text-sm text-hull-300">
                {processor.purpose}
                {processor.note ? (
                  <span className="mt-1 block text-sm text-hull-400">{processor.note}</span>
                ) : null}
              </span>
              <span className="shrink-0 text-sm font-medium text-white">
                {processor.name ? (
                  processor.url ? (
                    <Outbound href={processor.url}>{processor.name}</Outbound>
                  ) : (
                    processor.name
                  )
                ) : (
                  <Pending>provider</Pending>
                )}
              </span>
            </li>
          ))}
        </ul>
        <P>
          We will update this list before adding a processor that handles personal data. If
          you would like advance notice of changes, ask at{' '}
          <Mail address={COMPANY.email.support} />.
        </P>
      </>
    ),
  },
  {
    id: 'disclosures',
    title: 'Other disclosures',
    body: (
      <P>
        Beyond those processors we disclose personal data only where we must: to comply with a
        law or a valid legal request, to establish or defend legal claims, to protect the
        rights and safety of our users or the public, or to a buyer as part of a merger or
        acquisition — in which case you will be told, and this policy continues to apply until
        replaced by one no less protective.
      </P>
    ),
  },
  {
    id: 'transfers',
    title: 'International transfers',
    body: (
      <P>
        Our processors may handle data outside the country you are in, including outside the
        UK and EEA. Where that happens we rely on an approved transfer mechanism such as the
        UK International Data Transfer Agreement or the EU Standard Contractual Clauses,
        together with the safeguards in each processor&rsquo;s own terms. Your account,
        projects and evidence live on the hosting named above; where you configure a model
        provider of your own, the data you send it goes wherever that provider operates, on
        terms between you and them. Ask us for the current region at{' '}
        <Mail address={COMPANY.email.support} />.
      </P>
    ),
  },
  {
    id: 'retention',
    title: 'How long we keep it',
    body: (
      <Bullets>
        <Bullet>
          <strong className="text-white">Account data</strong> — while the account exists, and
          for 30 days after deletion so an accidental deletion can be undone.
        </Bullet>
        <Bullet>
          <strong className="text-white">QA runs, reports and evidence</strong> — while the
          account exists, unless you delete them sooner. Deleting a project deletes its
          evidence.
        </Bullet>
        <Bullet>
          <strong className="text-white">Metering and billing records</strong> — for as long as
          tax and accounting law requires, typically six or seven years, and stripped of
          anything not needed for that purpose.
        </Bullet>
        <Bullet>
          <strong className="text-white">Security and audit logs</strong> — up to 12 months,
          longer only where an open investigation requires it.
        </Bullet>
        <Bullet>
          <strong className="text-white">Backups</strong> — deletions propagate to backups
          within the backup rotation, and no restored copy is returned to live use with data
          you asked us to remove.
        </Bullet>
      </Bullets>
    ),
  },
  {
    id: 'security',
    title: 'How we protect it',
    body: (
      <P>
        Passwords are hashed, your email address is encrypted in the database with a key the
        database itself does not hold, agent credentials are stored hashed and can be rotated
        or revoked at any time, transport is encrypted, sessions are short-lived with a
        strictly-scoped refresh cookie, and security-relevant actions are recorded in an audit
        trail. Our security posture is described in the{' '}
        <Internal to="/docs">documentation</Internal>. No system is perfectly secure; if you
        find a weakness, report it privately to <Mail address={COMPANY.email.security} />{' '}
        rather than testing against other customers.
      </P>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your rights',
    body: (
      <>
        <P>
          Depending on where you live, you may have the right to access a copy of your data,
          correct it, delete it, restrict or object to processing, receive it in a portable
          format, and withdraw consent where processing relies on consent. You also have the
          right not to be discriminated against for exercising these rights.
        </P>
        <P>
          Email <Mail address={COMPANY.email.support} /> and we will respond within one month.
          We may need to verify who you are first — an account holder asking about their own
          data usually just needs to write from the account address.
        </P>
        <P>
          If you are unhappy with our response you can complain to your data protection
          authority; in the UK that is the Information Commissioner&rsquo;s Office. We would
          rather you told us first, so we can put it right.
        </P>
        <P>
          That includes billing data. We sell directly, so a request about an invoice or a
          payment comes to the same address as any other &mdash; there is no reseller holding
          a separate copy of your order.
        </P>
      </>
    ),
  },
  {
    id: 'children',
    title: 'Children',
    body: (
      <P>
        {COMPANY.product} is a tool for engineering teams and is not directed at children. We
        do not knowingly collect data from anyone under 18. If you believe a child has created
        an account, tell us and we will remove it.
      </P>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    body: (
      <P>
        We will update this policy as the service changes. The date at the top shows the last
        revision, and we will email account holders about material changes — such as adding a
        processor that handles your content — before they take effect.
      </P>
    ),
  },
];

export function Privacy() {
  usePageTitle('Privacy Policy — WaveQA');

  return (
    <LegalDocument
      eyebrow="Legal"
      title="Privacy Policy"
      intro={
        <>
          What {COMPANY.product} collects, why, who else touches it, and how to get it back or
          have it deleted. Written from what the software actually does rather than from a
          template — including the parts where the answer is &ldquo;nothing&rdquo;.
        </>
      }
      updated={UPDATED}
      sections={SECTIONS}
    />
  );
}
